/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ItemInputDetail, ItemInfo, PictoChartItem } from './PictoChart.type';

/**
 * Returns the item based on item info in a picto chart.
 * @param itemInfo
 * @param items
 * @returns
 */
function getItem<K, D extends PictoChartItem<K>>(itemInfo: ItemInfo, items: D[]) {
  const { idx } = itemInfo;
  return items[idx];
}

/**
 * Returns the next item's info in a picto chart.
 * @param itemInfo
 * @param items
 * @returns
 */
function getNextItemInfo<K, D extends PictoChartItem<K>>(itemInfo: ItemInfo, items: D[]) {
  let idx = itemInfo.idx;

  idx = Math.min(items.length - 1, idx + 1);
  return { idx: idx };
}

/**
 * Returns the previous item's info in a picto chart.
 * @param itemInfo
 * @returns
 */
function getPrevItemInfo(itemInfo: ItemInfo) {
  let idx = itemInfo.idx;

  idx = Math.max(0, idx - 1);
  return { idx: idx };
}

/**
 * Returns utils functions for navigation in a picto chart.
 * @param items
 * @returns
 */
export function getPictoChartNavUtils<K, D extends PictoChartItem<K>>(items: D[]) {
  return {
    getItem: (itemInfo: ItemInfo) => {
      return getItem(itemInfo, items);
    },
    getDetailFromInfo: (itemInfo?: ItemInfo): ItemInputDetail<K> => {
      if (!itemInfo) {
        return { id: undefined };
      }
      const item = getItem(itemInfo, items);
      return { id: item ? item.id : undefined };
    },
    getPrevItemInfo: (itemInfo: ItemInfo) => {
      return getPrevItemInfo(itemInfo);
    },
    getNextItemInfo: (itemInfo: ItemInfo) => {
      return getNextItemInfo(itemInfo, items);
    }
  };
}
